import React, { useEffect, useState, useCallback } from "react";
import { useTranslation, useToast } from "@familyzone/component-library";
import PageWithHeader from "../../templates/PageWithHeader";
import TabbedCardPage from "../../templates/TabbedCardPage";
import GeneralSettingsTab from "./GeneralSettingsTab";
import ImageSettingsTab from "./ImageSettingsTab";
import VideoSettingsTab from "./VideoSettingsTab";
import { getContentAwareConfig, updateContentAwareConfig, ContentAwareConfig } from "../../../utils/api/ContentAware";
import { useContentAwareStore } from "../../../storez/ContentAwareStore";
import { calculateTrialPeriodDetails } from "../../../utils/DateTimeUtil";
import TextSettingsTab from "./TextSettingsTab";
import ConfirmationModal from "../../shared/ConfirmationModal";

interface ContentAwareConfigPageProps {
  videoBlurringFeatureEnabled?: boolean;
  textAnalysisFeatureEnabled?: boolean;
}

export enum TabTypes {
  GENERAL = "general",
  IMAGE = "image",
  VIDEO = "video",
  TEXT = "text",
}

const ContentAwareConfigPage = ({ textAnalysisFeatureEnabled }: ContentAwareConfigPageProps): JSX.Element => {
  const { t } = useTranslation();
  const { successToast, errorToast } = useToast();

  const breadcrumbs = [
    { title: t("Filtering"), url: "/filtering", isActive: false },
    { title: t("Content Aware"), isActive: true },
  ];

  const DEFAULTS = {
    guiltByAssociation: false,
    excludedDomains: [] as string[],
    image: {
      gore: { enabled: false, sensitivity: -1 },
      porn: { enabled: true, sensitivity: 0.5 },
      intimateApparel: { enabled: true, sensitivity: 0 },
    },
    video: {
      gore: { enabled: false, sensitivity: -1 },
      porn: { enabled: true, sensitivity: 0.5 },
      intimateApparel: { enabled: true, sensitivity: 0 },
      blockVideo: false,
      youtubeExceptions: [] as string[],
    },
    text: {
      enabled: false,
    },
  };

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [resetToDefaultModal, setResetToDefaultModal] = useState<string | undefined>(undefined);
  const [currentConfig, setCurrentConfig] = useState<ContentAwareConfig | undefined>(undefined);

  // General settings
  const [guiltByAssociationEnabled, setGuiltByAssociationEnabled] = useState<boolean>(DEFAULTS.guiltByAssociation);
  const [excludedDomains, setExcludedDomains] = useState<string[]>(DEFAULTS.excludedDomains);

  // Image
  const [goreDetectionEnabled, setGoreDetectionEnabled] = useState<boolean>(DEFAULTS.image.gore.enabled);
  const [goreDetectionSensitivity, setGoreDetectionSensitivity] = useState<number>(DEFAULTS.image.gore.sensitivity);
  const [pornDetectionEnabled, setPornDetectionEnabled] = useState<boolean>(DEFAULTS.image.porn.enabled);
  const [pornDetectionSensitivity, setPornDetectionSensitivity] = useState<number>(DEFAULTS.image.porn.sensitivity);
  const [intimateApparelDetectionEnabled, setIntimateApparelDetectionEnabled] = useState<boolean>(DEFAULTS.image.intimateApparel.enabled);
  const [intimateApparelDetectionSensitivity, setIntimateApparelDetectionSensitivity] = useState<number>(
    DEFAULTS.image.intimateApparel.sensitivity
  );

  // Video
  const [videoGoreDetection, setVideoGoreDetectionEnabled] = useState<boolean>(DEFAULTS.video.gore.enabled);
  const [videoGoreSensitivity, setVideoGoreSensitivity] = useState<number>(DEFAULTS.video.gore.sensitivity);
  const [videoPornDetection, setVideoPornDetectionEnabled] = useState<boolean>(DEFAULTS.video.porn.enabled);
  const [videoPornSensitivity, setVideoPornSensitivity] = useState<number>(DEFAULTS.video.porn.sensitivity);
  const [videoIntimateApparelDetection, setVideoIntimateApparelDetectionEnabled] = useState<boolean>(
    DEFAULTS.video.intimateApparel.enabled
  );
  const [videoIntimateApparelSensitivity, setVideoIntimateApparelSensitivity] = useState<number>(
    DEFAULTS.video.intimateApparel.sensitivity
  );
  const [blockVideo, setBlockVideo] = useState<boolean>(DEFAULTS.video.blockVideo);
  const [videoYoutubeChannelExceptions, setVideoYoutubeChannelExceptions] = useState<string[]>(DEFAULTS.video.youtubeExceptions);

  // Text
  const [enableTextAnalysis, setEnableTextAnalysis] = useState<boolean>(DEFAULTS.text.enabled);

  const fetchContentAwareLicense = useContentAwareStore((state) => state.fetchContentAwareLicense);
  const contentAwareLicense = useContentAwareStore((state) => state.license);

  // Check if the license is not expired
  const licenseIsNotExpired: boolean = (() => {
    if (contentAwareLicense?.startDate && contentAwareLicense?.endDate) {
      const { trialDaysLapsed } = calculateTrialPeriodDetails(contentAwareLicense.startDate, contentAwareLicense.endDate);
      return trialDaysLapsed > 0;
    }
    return true;
  })();

  // Disable controls if the trial license is not active, or if the page is loading or saving
  const controlsDisabled: boolean = !licenseIsNotExpired || loading || saving;

  const fetchContentAwareConfig = useCallback(() => {
    setLoading(true);
    getContentAwareConfig()
      .then((config) => {
        const goreDetectionEnabled = config.defaultRules.gore !== -1;
        const pornDetectionEnabled = config.defaultRules.porn !== -1;
        const intimateApparelDetectionEnabled = config.defaultRules.swimwear !== -1;
        const goreVideoEnabled = config.defaultRules.goreVideo !== -1;
        const pornVideoEnabled = config.defaultRules.pornVideo !== -1;
        const intimateApparelVideoEnabled = config.defaultRules.swimwearVideo !== -1;

        setGoreDetectionEnabled(goreDetectionEnabled);
        setGoreDetectionSensitivity(goreDetectionEnabled ? config.defaultRules.gore : -1);

        setPornDetectionEnabled(pornDetectionEnabled);
        setPornDetectionSensitivity(pornDetectionEnabled ? config.defaultRules.porn : 0.5);

        setIntimateApparelDetectionEnabled(intimateApparelDetectionEnabled);
        setIntimateApparelDetectionSensitivity(intimateApparelDetectionEnabled ? config.defaultRules.swimwear : 0.5);

        setGuiltByAssociationEnabled(config.guiltByAssociationEnabled);
        setExcludedDomains(config.neverRunOn);

        setVideoGoreDetectionEnabled(goreVideoEnabled);
        // if goreVideo detection enabled
        if (goreVideoEnabled) setVideoGoreSensitivity(config.defaultRules.goreVideo ?? -1);

        setVideoPornDetectionEnabled(pornVideoEnabled);
        if (pornVideoEnabled) setVideoPornSensitivity(config.defaultRules.pornVideo ?? 0.5);

        setVideoIntimateApparelDetectionEnabled(intimateApparelVideoEnabled);
        if (intimateApparelVideoEnabled) setVideoIntimateApparelSensitivity(config.defaultRules.swimwearVideo ?? 0.5);

        setBlockVideo(config.defaultRules.blockVideo ?? false);
        setVideoYoutubeChannelExceptions(config.youtubeWhitelist ?? []);

        setEnableTextAnalysis(config.textAnalysis?.enabled ?? false);

        setCurrentConfig(config);
      })
      .catch((err) => {
        let errorMessage: string | undefined;
        if (err instanceof Error) {
          errorMessage = err.message;
          if (errorMessage === "Content Aware config not found") return;
        }
        errorToast({
          title: t("Couldn't fetch Content Aware config"),
          description: errorMessage || "Unexpected issue fetching config, please contact Support if this issue persists.",
          isClosable: true,
          position: "bottom-right",
        });
      })
      .finally(() => setLoading(false));
  }, [t, errorToast]);

  useEffect(() => {
    fetchContentAwareConfig();
  }, [fetchContentAwareConfig]);

  useEffect(() => {
    void fetchContentAwareLicense();
  }, [fetchContentAwareLicense]);

  function saveContentAwareConfig(tabType: TabTypes, reset = false): void {
    if (!currentConfig) return;
    setSaving(true);
    const contentAwareConfig: ContentAwareConfig = currentConfig;

    switch (tabType) {
      case TabTypes.GENERAL:
        contentAwareConfig.guiltByAssociationEnabled = guiltByAssociationEnabled;
        contentAwareConfig.neverRunOn = excludedDomains;
        break;
      case TabTypes.IMAGE:
        if (reset) {
          setGoreDetectionEnabled(DEFAULTS.image.gore.enabled);
          setGoreDetectionSensitivity(DEFAULTS.image.gore.sensitivity);
          setPornDetectionEnabled(DEFAULTS.image.porn.enabled);
          setPornDetectionSensitivity(DEFAULTS.image.porn.sensitivity);
          setIntimateApparelDetectionEnabled(DEFAULTS.image.intimateApparel.enabled);
          setIntimateApparelDetectionSensitivity(DEFAULTS.image.intimateApparel.sensitivity);

          contentAwareConfig.defaultRules.gore = DEFAULTS.image.gore.sensitivity;
          contentAwareConfig.defaultRules.porn = DEFAULTS.image.porn.sensitivity;
          contentAwareConfig.defaultRules.swimwear = DEFAULTS.image.intimateApparel.sensitivity;
        } else {
          contentAwareConfig.defaultRules.gore = goreDetectionEnabled ? goreDetectionSensitivity : -1;
          contentAwareConfig.defaultRules.porn = pornDetectionEnabled ? pornDetectionSensitivity : -1;
          contentAwareConfig.defaultRules.swimwear = intimateApparelDetectionEnabled ? intimateApparelDetectionSensitivity : -1;
        }
        break;
      case TabTypes.VIDEO:
        if (reset) {
          setVideoGoreDetectionEnabled(DEFAULTS.video.gore.enabled);
          setVideoGoreSensitivity(DEFAULTS.video.gore.sensitivity);
          setVideoPornDetectionEnabled(DEFAULTS.video.porn.enabled);
          setVideoPornSensitivity(DEFAULTS.video.porn.sensitivity);
          setVideoIntimateApparelDetectionEnabled(DEFAULTS.video.intimateApparel.enabled);
          setVideoIntimateApparelSensitivity(DEFAULTS.video.intimateApparel.sensitivity);
          setBlockVideo(DEFAULTS.video.blockVideo);
          setVideoYoutubeChannelExceptions(DEFAULTS.video.youtubeExceptions);

          contentAwareConfig.defaultRules.goreVideo = DEFAULTS.video.gore.sensitivity;
          contentAwareConfig.defaultRules.pornVideo = DEFAULTS.video.porn.sensitivity;
          contentAwareConfig.defaultRules.swimwearVideo = DEFAULTS.video.intimateApparel.sensitivity;
          contentAwareConfig.defaultRules.blockVideo = DEFAULTS.video.blockVideo;
          contentAwareConfig.youtubeWhitelist = DEFAULTS.video.youtubeExceptions;
        } else {
          contentAwareConfig.defaultRules.goreVideo = videoGoreDetection ? videoGoreSensitivity : -1;
          contentAwareConfig.defaultRules.pornVideo = videoPornDetection ? videoPornSensitivity : -1;
          contentAwareConfig.defaultRules.swimwearVideo = videoIntimateApparelDetection ? videoIntimateApparelSensitivity : -1;
          contentAwareConfig.defaultRules.blockVideo = blockVideo;
          contentAwareConfig.youtubeWhitelist = videoYoutubeChannelExceptions;
        }
        break;
      case TabTypes.TEXT:
        contentAwareConfig.textAnalysis = {
          enabled: enableTextAnalysis,
        };
        break;

      default:
        break;
    }

    if (excludedDomains.length > 50) {
      errorToast({
        title: t("Couldn't save Content Aware config"),
        description:
          "The number of excluded domains exceeds the maximum allowed amount. Please make sure that there are no more than 50 domains entered.",
        isClosable: true,
        position: "bottom-right",
      });
      setSaving(false);
      return;
    }

    if (videoYoutubeChannelExceptions.length > 50) {
      errorToast({
        title: t("Couldn't save Content Aware config"),
        description:
          "The number of whitelisted Youtube channels exceeds the maximum allowed amount. Please make sure that there are no more than 50 channels entered.",
        isClosable: true,
        position: "bottom-right",
      });
      setSaving(false);
      return;
    }

    updateContentAwareConfig(contentAwareConfig)
      .then(() => {
        const settingsType = tabType.charAt(0).toUpperCase() + tabType.slice(1);
        if (reset) {
          successToast({
            title: t(`${settingsType} settings have been reset to default`),
            isClosable: true,
            position: "bottom-right",
          });
        } else {
          successToast({
            title: t(`${settingsType} settings have been updated`),
            isClosable: true,
            position: "bottom-right",
          });
        }
      })
      .catch((err) => {
        let errorMessage;
        if (err instanceof Error) {
          errorMessage = err.message;
        }
        errorToast({
          title: t("Couldn't save Content Aware config"),
          description: errorMessage || "Unexpected issue saving config, please contact Support if this issue persists.",
          isClosable: true,
          position: "bottom-right",
        });
      })
      .finally(() => {
        setSaving(false);
      });

    setCurrentConfig(contentAwareConfig);

    return;
  }

  const isImageSettingsDefault = () => {
    return (
      goreDetectionEnabled === DEFAULTS.image.gore.enabled &&
      goreDetectionSensitivity === DEFAULTS.image.gore.sensitivity &&
      pornDetectionEnabled === DEFAULTS.image.porn.enabled &&
      pornDetectionSensitivity === DEFAULTS.image.porn.sensitivity &&
      intimateApparelDetectionEnabled === DEFAULTS.image.intimateApparel.enabled &&
      intimateApparelDetectionSensitivity === DEFAULTS.image.intimateApparel.sensitivity
    );
  };

  const isVideoSettingsDefault = () => {
    return (
      videoGoreDetection === DEFAULTS.video.gore.enabled &&
      videoGoreSensitivity === DEFAULTS.video.gore.sensitivity &&
      videoPornDetection === DEFAULTS.video.porn.enabled &&
      videoPornSensitivity === DEFAULTS.video.porn.sensitivity &&
      videoIntimateApparelDetection === DEFAULTS.video.intimateApparel.enabled &&
      videoIntimateApparelSensitivity === DEFAULTS.video.intimateApparel.sensitivity &&
      blockVideo === DEFAULTS.video.blockVideo &&
      videoYoutubeChannelExceptions.length === 0
    );
  };

  const tabs = [
    {
      key: TabTypes.GENERAL,
      label: t("General settings"),
      content: (
        <GeneralSettingsTab
          saving={saving}
          disabled={controlsDisabled}
          onSave={saveContentAwareConfig}
          excludedDomains={excludedDomains}
          setExcludedDomains={setExcludedDomains}
          guiltByAssociationEnabled={guiltByAssociationEnabled}
          setGuiltByAssociationEnabled={setGuiltByAssociationEnabled}
        />
      ),
    },
    {
      key: TabTypes.IMAGE,
      label: t("Image"),
      content: (
        <ImageSettingsTab
          saving={saving}
          disabled={controlsDisabled}
          onSave={saveContentAwareConfig}
          onReset={() => setResetToDefaultModal(TabTypes.IMAGE)}
          resetSettingsDisabled={isImageSettingsDefault()}
          goreDetectionEnabled={goreDetectionEnabled}
          setGoreDetectionEnabled={(enabled) => {
            setGoreDetectionEnabled(enabled);
            // if enabling gore detection, set sensitivity to 0 (low)
            // so a valid value is presented to the user.
            if (enabled) setGoreDetectionSensitivity(0);
            // if disabling gore detection, set sensitivity to -1 to
            // match the default value.
            if (!enabled) setGoreDetectionSensitivity(-1);
          }}
          goreDetectionSensitivity={goreDetectionSensitivity}
          setGoreDetectionSensitivity={setGoreDetectionSensitivity}
          pornDetectionEnabled={pornDetectionEnabled}
          setPornDetectionEnabled={setPornDetectionEnabled}
          pornDetectionSensitivity={pornDetectionSensitivity}
          setPornDetectionSensitivity={setPornDetectionSensitivity}
          intimateApparelDetectionEnabled={intimateApparelDetectionEnabled}
          setIntimateApparelDetectionEnabled={setIntimateApparelDetectionEnabled}
          intimateApparelDetectionSensitivity={intimateApparelDetectionSensitivity}
          setIntimateApparelDetectionSensitivity={setIntimateApparelDetectionSensitivity}
        />
      ),
    },
    {
      key: TabTypes.VIDEO,
      label: t("Video"),
      content: (
        <VideoSettingsTab
          saving={saving}
          disabled={controlsDisabled}
          onSave={saveContentAwareConfig}
          onReset={() => setResetToDefaultModal(TabTypes.VIDEO)}
          resetSettingsDisabled={isVideoSettingsDefault()}
          videoGoreDetection={videoGoreDetection}
          setVideoGoreDetectionEnabled={(enabled) => {
            setVideoGoreDetectionEnabled(enabled);
            // if enabling gore detection, set sensitivity to 0 (low)
            // so a valid value is presented to the user.
            if (enabled) setVideoGoreSensitivity(0);
            // if disabling gore detection, set sensitivity to -1 to
            // match the default value.
            if (!enabled) setVideoGoreSensitivity(-1);
          }}
          videoGoreSensitivity={videoGoreSensitivity}
          setVideoGoreSensitivity={setVideoGoreSensitivity}
          videoPornDetection={videoPornDetection}
          setVideoPornDetectionEnabled={setVideoPornDetectionEnabled}
          videoPornSensitivity={videoPornSensitivity}
          setVideoPornSensitivity={setVideoPornSensitivity}
          videoIntimateApparelDetection={videoIntimateApparelDetection}
          setVideoIntimateApparelDetectionEnabled={setVideoIntimateApparelDetectionEnabled}
          videoIntimateApparelSensitivity={videoIntimateApparelSensitivity}
          setVideoIntimateApparelSensitivity={setVideoIntimateApparelSensitivity}
          videoBlockVideo={blockVideo}
          setVideoBlockVideo={setBlockVideo}
          videoYoutubeChannelExceptions={videoYoutubeChannelExceptions}
          setVideoYoutubeChannelExceptions={setVideoYoutubeChannelExceptions}
        />
      ),
    },
  ];

  if (!!textAnalysisFeatureEnabled) {
    tabs.push({
      key: TabTypes.TEXT,
      label: t("Text"),
      content: (
        <TextSettingsTab
          saving={saving}
          disabled={controlsDisabled}
          onSave={saveContentAwareConfig}
          textAnalysisEnabled={enableTextAnalysis}
          setTextAnalysisEnabled={setEnableTextAnalysis}
        />
      ),
    });
  }

  return (
    <PageWithHeader breadcrumbs={breadcrumbs} title={t("Content Aware")}>
      <TabbedCardPage tabs={tabs} defaultKey="general" />
      <ConfirmationModal
        open={!!resetToDefaultModal}
        onClose={() => {
          setResetToDefaultModal(undefined);
        }}
        onConfirm={() => {
          if (resetToDefaultModal === TabTypes.IMAGE) {
            saveContentAwareConfig(TabTypes.IMAGE, true);
          } else if (resetToDefaultModal === TabTypes.VIDEO) {
            saveContentAwareConfig(TabTypes.VIDEO, true);
          }
          setResetToDefaultModal(undefined);
        }}
        heading={`Reset ${resetToDefaultModal ?? ""} settings`}
        description={`This action will permanently delete all your custom ${
          resetToDefaultModal ?? ""
        } settings and restore the system default settings.`}
        confirmationCheckbox=""
        CTALabel={`Restore default ${resetToDefaultModal ?? ""} settings`}
      />
    </PageWithHeader>
  );
};

export default ContentAwareConfigPage;
